<script lang="ts" module>
  export type Tab = "inbox" | "triage" | "tracker" | "crm" | "settings";
</script>

<script lang="ts">
  /**
   * Tab bar fixe, cinq onglets — la navigation racine de l'app.
   *
   * Elle remplace le mélange d'avant : deux icônes dans la topbar (tracker,
   * CRM) plus un menu `⋯`. Trois destinations sur cinq n'étaient atteignables
   * que par un geste appris ; ici tout est visible d'un coup d'œil, et chaque
   * onglet porte son propre en-tête, ce qui libère la topbar (elle ne sert
   * plus que dans les sous-vues, pour revenir).
   *
   * Géométrie du handoff : grille de 5, 52px de haut minimum, fond translucide
   * flouté, filet en haut, et le `env(safe-area-inset-bottom)` qui garde la
   * barre au-dessus du home indicator.
   */

  import Icon, { type IconName } from "./shared/Icon.svelte";
  import { haptic } from "../gestures/haptic";

  interface Props {
    active: Tab;
    onSelect: (tab: Tab) => void;
    /** Compteur de non-lus, sur la Boîte. 0 = pas de pastille. */
    unread?: number;
    /** Nombre de fils en file de triage. */
    triage?: number;
  }

  let { active, onSelect, unread = 0, triage = 0 }: Props = $props();

  const TABS: Array<{ id: Tab; label: string; icon: IconName }> = [
    { id: "inbox", label: "Boîte", icon: "rail-inbox" },
    { id: "triage", label: "Triage", icon: "rail-tasks" },
    { id: "tracker", label: "Suivi", icon: "rail-tracking" },
    { id: "crm", label: "Sociétés", icon: "rail-crm" },
    { id: "settings", label: "Réglages", icon: "settings" },
  ];

  function badgeFor(id: Tab): number {
    if (id === "inbox") return unread;
    if (id === "triage") return triage;
    return 0;
  }

  function pick(tab: Tab) {
    if (tab === active) return;
    haptic("light");
    onSelect(tab);
  }
</script>

<nav class="tabbar" aria-label="Navigation principale">
  {#each TABS as t (t.id)}
    {@const badge = badgeFor(t.id)}
    <button
      class="tab"
      class:on={active === t.id}
      aria-current={active === t.id ? "page" : undefined}
      aria-label={badge > 0 ? `${t.label}, ${badge}` : t.label}
      onclick={() => pick(t.id)}
    >
      <span class="glyph">
        <Icon name={t.icon} size={20} />
        {#if badge > 0}
          <span class="badge">{badge > 99 ? "99+" : badge}</span>
        {/if}
      </span>
      <span class="label">{t.label}</span>
    </button>
  {/each}
</nav>

<style>
  .tabbar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 40;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    background: color-mix(in srgb, var(--bg) 90%, transparent);
    backdrop-filter: saturate(180%) blur(22px);
    -webkit-backdrop-filter: saturate(180%) blur(22px);
    border-top: 1px solid var(--border-subtle);
    /* La barre est en `fixed`, donc ancrée au viewport et non au body :
       c'est à elle de réserver la place du home indicator. */
    padding-bottom: env(safe-area-inset-bottom);
  }

  .tab {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    min-height: 52px;
    padding: 6px 0;
    color: var(--fg-subtle);
    transition: var(--tr);
  }
  .tab.on { color: var(--fg); }
  .tab:active { background: var(--bg-hover); }

  .glyph {
    position: relative;
    display: block;
    line-height: 0;
  }

  .label {
    font-size: 9.5px;
    font-weight: 550;
    letter-spacing: 0;
  }

  .badge {
    position: absolute;
    top: -4px;
    right: -9px;
    min-width: 15px;
    height: 15px;
    padding: 0 3px;
    border-radius: var(--radius-pill);
    background: var(--accent);
    color: var(--accent-fg);
    font-family: var(--font-mono);
    font-size: 9px;
    font-variant-numeric: tabular-nums;
    line-height: 15px;
    text-align: center;
  }
</style>
